import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateTermsAndConditionsMutation } from "@src/api/LoginApiSlice";
import { setUserDetails } from "@src/app/globalSlice";
import { clearLogoutSession } from "@src/utils/logoutUtils";
import { storageService } from "@src/services/StorageService";
import { isElectronApp } from "@src/utils/envUtils";
import { toast, Typography } from "top-brains-ui";
import { Camera, ScanEye, Globe, ShieldCheck } from "lucide-react";
import GradiantButton from "@src/components/gradiant-btn/GradiantButton";
import CommonLoader from "@src/components/loader/CommonLoader";
import { COMMON_COLORS } from "@src/styles/ColorConstants";
import { RootState } from "@src/app/store";
import styles from "./TermsAndConditions.module.scss";
import { stopDockerDesktopSilently } from "@src/utils/devopsUtils";

const TermsAndConditions: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isElectron = isElectronApp();

  const userDetails = useSelector(
    (state: RootState) => state.globalSlice.userDetails,
  );

  const [updateTerms, { isLoading }] = useUpdateTermsAndConditionsMutation();

  const handleContinue = async () => {
    if (!agreed) {
      toast.error("Please accept the terms and conditions to proceed.");
      return;
    }

    try {
      await updateTerms({ agreedTermsAndCondition: true }).unwrap();
      
      // Update the user details locally
      if (userDetails) {
        const updatedUserDetails = {
          ...userDetails,
          agreedTermsAndCondition: true,
          user: userDetails.user
            ? { ...userDetails.user, agreedTermsAndCondition: true }
            : undefined,
        };
        dispatch(setUserDetails(updatedUserDetails));
      }

      toast.success("Terms agreed successfully!");
      navigate("/learn");
    } catch (error) {
      console.error("Failed to update terms status:", error);
      toast.error("Failed to update terms and conditions. Please try again.");
    }
  };

  const handleExit = async () => {
    setIsExiting(true);
    (window as unknown as { isExiting?: boolean }).isExiting = true;
    try {
      const activeUser = userDetails?.name || "guest";
      await stopDockerDesktopSilently(activeUser);
    } catch (error) {
      console.error("Failed to stop Docker on exit:", error);
    }
    try {
      const envFolder = import.meta.env.VITE_EXTRACTED_RESOURCE_FOLDER;
      if (envFolder) {
        await storageService.deletePath([envFolder]);
      }
    } catch (error) {
      console.error("Failed to delete extracted resources on exit:", error);
    }
    if (isElectron) {
      clearLogoutSession(dispatch, { theme: "app-theme" });
      (window as unknown as { electronApp: { quitApp: () => void } }).electronApp.quitApp();
    } else {
      setIsExiting(false);
      (window as unknown as { isExiting?: boolean }).isExiting = false;
      navigate("/login");
    }
  };

  return (
    <div className={styles.termsWrapper}>
      <div className={styles.termsCard}>
        <div className={styles.header}>
          <Typography fontSize={26} fontWeight="bold" className={styles.title}>
            System User Agreement
          </Typography>
          <Typography fontSize={13} className={styles.subtitle}>
            Please read and agree to the following terms and monitoring policies to access your dashboard.
          </Typography>
        </div>

        <div className={styles.termsContent}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Camera size={18} color="#434db8" />
              <Typography fontSize={14} fontWeight="semi-bold" className={styles.sectionTitle}>
                1. Mandatory Camera & Video Capturing
              </Typography>
            </div>
            <Typography fontSize={12} className={styles.sectionText}>
              By proceeding, you authorize the application to capture video feeds through your device camera for proctoring purposes. The camera must remain active and unobstructed at all times. Disabling or blocking your camera will lock your session.
            </Typography>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <ScanEye size={18} color="#434db8" />
              <Typography fontSize={14} fontWeight="semi-bold" className={styles.sectionTitle}>
                2. Real-Time Object & Person Detection
              </Typography>
            </div>
            <Typography fontSize={12} className={styles.sectionText}>
              The application utilizes automated AI-based detection to identify unauthorized objects (such as mobile devices, secondary screens, or printed materials) and verify the presence of a single candidate. Any violations will trigger real-time alerts.
            </Typography>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Globe size={18} color="#434db8" />
              <Typography fontSize={14} fontWeight="semi-bold" className={styles.sectionTitle}>
                3. Network & IP Address Restrictions
              </Typography>
            </div>
            <Typography fontSize={12} className={styles.sectionText}>
              Access is permitted only under approved organization IP networks. The use of proxy servers, VPNs, or network spoofing/tunneling configurations is strictly prohibited and will prevent login or result in automatic suspension.
            </Typography>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <ShieldCheck size={18} color="#434db8" />
              <Typography fontSize={14} fontWeight="semi-bold" className={styles.sectionTitle}>
                4. Session & System Integrity
              </Typography>
            </div>
            <Typography fontSize={12} className={styles.sectionText}>
              Only a single active login session is permitted. Tab switches, window minimization, screen recording, or unauthorized background processes are actively monitored to ensure the integrity of the educational evaluation.
            </Typography>
          </div>
        </div>

        <div className={styles.checkboxContainer}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className={styles.realCheckbox}
            />
            <span className={styles.customCheckbox}></span>
            <span className={styles.labelMarkup}>
              I have read, understood, and agree to the System User Agreement.
            </span>
          </label>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={styles.exitBtn}
            onClick={handleExit}
            disabled={isLoading || isExiting}
          >
            Exit
          </button>
          <GradiantButton
            variant="primary"
            onClick={handleContinue}
            disabled={!agreed || isLoading || isExiting}
            width="150px"
            height="40px"
            label="Continue"
            className={styles.continueBtn}
          />
        </div>
      </div>

      {(isLoading || isExiting) && (
        <CommonLoader
          color={COMMON_COLORS.WEBSITE_GREEN_LIGHT}
          overLayColor={COMMON_COLORS.LOADER_OVERLAY}
        />
      )}
    </div>
  );
};

export default TermsAndConditions;
